import { Routes } from '@angular/router';
import { RouteConstants } from './shared/constants/route-constants';
import { PageRuleListComponent } from './features/rules/pages/page-rule-list/page-rule-list.component';
import { PageRuleFormComponent } from './features/rules/pages/page-rule-form/page-rule-form.component';
import { PageRuleDetailComponent } from './features/rules/pages/page-rule-detail/page-rule-detail.component';
import { PageDashboardComponent } from './features/dashboard/pages/page-dashboard/page-dashboard.component';
import { PageDeviceListComponent } from './features/devices/pages/page-device-list/page-device-list.component';
import { PageDeviceDetailComponent } from './features/devices/pages/page-device-detail/page-device-detail.component';
import { PageCisternListComponent } from './features/cisterns/pages/page-cistern-list/page-cistern-list.component';
import { PageCisternDetailComponent } from './features/cisterns/pages/page-cistern-detail/page-cistern-detail.component';
import { PageStatisticsComponent } from './features/statistics/pages/page-statistics/page-statistics.component';
import { PageWeatherComponent } from './features/weather/pages/page-weather/page-weather.component';
import { PageMapComponent } from './features/map/pages/page-map/page-map.component';
import { PageErrorComponent } from './shared/pages/page-error/page-error.component';
import { canDeactivateGuard } from './shared/guards/can-deactivate.guard';
import { CanActivateAuthGuardService } from '@sso/ld-sso-with-oauth2-oidc';
import { Role } from './core/enums/role.enum';
import { PageNotificationsComponent } from './features/notifications/pages/page-notifications/page-notifications.component';
import { PageImprintComponent } from './features/settings/pages/page-imprint/page-imprint.component';
import { PageTermsComponent } from './features/settings/pages/page-terms/page-terms.component';
import { PagePrivacyComponent } from './features/settings/pages/page-privacy/page-privacy.component';
import { PageDemoComponent } from './features/scenarios/pages/page-demo/page-demo.component';
import { defaultRedirectGuard } from './shared/guards/default-redirect.guard';
import { PageDeviceGroupListComponent } from './features/devices/pages/page-device-group-list/page-device-group-list.component';
import { PageDeviceGroupDetailComponent } from './features/devices/pages/page-device-group-detail/page-device-group-detail.component';
import { PageDemoLoginComponent } from './features/scenarios/pages/page-demo-login/page-demo-login.component';
import { PageSettingsComponent } from './features/settings/pages/page-settings/page-settings.component';
import { PageActionLogComponent } from './features/action-log/pages/page-action-log/page-action-log.component';
import { PagePumpListComponent } from './features/pumps/pages/page-pump-list/page-pump-list.component';
import { PagePumpDetailComponent } from './features/pumps/pages/page-pump-detail/page-pump-detail.component';

export const routes: Routes = [
  {
    path: RouteConstants.routeHome,
    pathMatch: 'full',
    canActivate: [defaultRedirectGuard],
    component: PageErrorComponent,
  },
  {
    path: RouteConstants.routeDashboard,
    pathMatch: 'full',
    title: 'navigation.dashboard.label',
    data: {
      breadcrumb: 'navigation.dashboard.label',
      headline: 'navigation.dashboard.headline',
      description: 'navigation.dashboard.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    component: PageDashboardComponent,
  },
  {
    path: RouteConstants.routeRule,
    title: 'navigation.rule.label',
    data: {
      breadcrumb: 'navigation.rule.label',
      headline: 'navigation.rule.headline',
      description: 'navigation.rule.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    children: [
      {
        path: '',
        component: PageRuleListComponent,
      },
      {
        path: RouteConstants.routeAdd,
        component: PageRuleFormComponent,
        canDeactivate: [canDeactivateGuard],
        data: {
          breadcrumb: 'navigation.addRule.label',
          headline: 'navigation.addRule.headline',
          description: 'navigation.addRule.description',
        },
      },
      {
        path: `${RouteConstants.routeUpdate}/${RouteConstants.routeId}`,
        component: PageRuleFormComponent,
        canDeactivate: [canDeactivateGuard],
        data: {
          breadcrumb: 'navigation.updateRule.label',
          headline: 'navigation.updateRule.headline',
          description: 'navigation.updateRule.description',
        },
      },
      {
        path: RouteConstants.routeId,
        data: { breadcrumb: 'navigation.breadcrumb.detail' },
        component: PageRuleDetailComponent,
      },
    ],
  },
  {
    path: RouteConstants.routeDevice,
    title: 'navigation.device.label',
    data: {
      breadcrumb: 'navigation.device.label',
      headline: 'navigation.device.headline',
      description: 'navigation.device.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    children: [
      {
        path: '',
        component: PageDeviceListComponent,
      },
      {
        path: RouteConstants.routeId,
        data: { breadcrumb: 'navigation.breadcrumb.detail' },
        component: PageDeviceDetailComponent,
      },
    ],
  },
  {
    path: RouteConstants.routeDeviceGroup,
    title: 'navigation.deviceGroup.label',
    data: {
      breadcrumb: 'navigation.deviceGroup.label',
      headline: 'navigation.deviceGroup.headline',
      description: 'navigation.deviceGroup.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    children: [
      {
        path: '',
        component: PageDeviceGroupListComponent,
      },
      {
        path: RouteConstants.routeId,
        data: { breadcrumb: 'navigation.breadcrumb.detail' },
        component: PageDeviceGroupDetailComponent,
      },
    ],
  },
  {
    path: RouteConstants.routeCistern,
    title: 'navigation.cistern.label',
    data: {
      breadcrumb: 'navigation.cistern.label',
      headline: 'navigation.cistern.headline',
      description: 'navigation.cistern.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    children: [
      {
        path: '',
        component: PageCisternListComponent,
      },
      {
        path: RouteConstants.routeId,
        data: { breadcrumb: 'navigation.breadcrumb.detail' },
        component: PageCisternDetailComponent,
      },
    ],
  },
  {
    path: RouteConstants.routePump,
    title: 'navigation.pump.label',
    data: {
      breadcrumb: 'navigation.pump.label',
      headline: 'navigation.pump.headline',
      description: 'navigation.pump.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    children: [
      {
        path: '',
        component: PagePumpListComponent,
      },
      {
        path: RouteConstants.routeId,
        data: { breadcrumb: 'navigation.breadcrumb.detail' },
        component: PagePumpDetailComponent,
      },
    ],
  },
  {
    path: RouteConstants.routeStatistics,
    title: 'navigation.statistics.label',
    data: {
      breadcrumb: 'navigation.statistics.label',
      headline: 'navigation.statistics.headline',
      description: 'navigation.statistics.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    component: PageStatisticsComponent,
  },
  {
    path: RouteConstants.routeWeather,
    title: 'navigation.weather.label',
    data: {
      breadcrumb: 'navigation.weather.label',
      headline: 'navigation.weather.headline',
      description: 'navigation.weather.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    component: PageWeatherComponent,
  },
  {
    path: RouteConstants.routeMap,
    title: 'navigation.map.label',
    data: {
      breadcrumb: 'navigation.map.label',
      headline: 'navigation.map.headline',
      description: 'navigation.map.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    component: PageMapComponent,
  },
  {
    path: RouteConstants.routeSettings,
    title: 'navigation.settings.label',
    data: {
      breadcrumb: 'navigation.settings.label',
      headline: 'navigation.settings.headline',
      description: 'navigation.settings.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    component: PageSettingsComponent,
  },
  {
    path: RouteConstants.routeNotifications,
    title: 'navigation.notification.label',
    data: {
      breadcrumb: 'navigation.notification.label',
      headline: 'navigation.notification.headline',
      description: 'navigation.notification.description',
      roles: [Role.RM_USER, Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    component: PageNotificationsComponent,
  },
  {
    path: RouteConstants.actionLog,
    title: 'navigation.actionLog.label',
    data: {
      breadcrumb: 'navigation.actionLog.label',
      headline: 'navigation.actionLog.headline',
      description: 'navigation.actionLog.description',
      roles: [Role.RM_ADMIN],
    },
    canActivate: [CanActivateAuthGuardService],
    component: PageActionLogComponent,
  },
  {
    path: RouteConstants.routeDemo,
    title: 'navigation.demo.label',
    data: {
      breadcrumb: 'navigation.demo.label',
      headline: 'navigation.demo.headline',
      description: 'navigation.demo.description',
      roles: [Role.RM_DEMO, Role.RM_DEMO_GROUP],
    },
    canActivate: [CanActivateAuthGuardService],
    component: PageDemoComponent,
  },
  {
    path: RouteConstants.routeDemoLogin,
    title: 'navigation.demo.label',
    data: {
      breadcrumb: 'navigation.demo.label',
      headline: 'navigation.demo.headline',
      description: 'navigation.demo.description',
    },
    component: PageDemoLoginComponent,
  },
  // {
  //   path: RouteConstants.routeOrganization,
  //   title: "navigation.organization.label",
  //   data: {
  //     breadcrumb: "navigation.organization.label",
  //     headline: "navigation.organization.headline",
  //     description: "navigation.organization.description",
  //     roles: [
  //       Role.RM_USER,
  //       Role.RM_ADMIN
  //     ],
  //   },
  //   canActivate: [
  //     CanActivateAuthGuardService
  //   ],
  //   component: PageOrganizationComponent
  // },
  {
    path: RouteConstants.routeImprint,
    title: 'navigation.imprint.label',
    data: {
      breadcrumb: 'navigation.imprint.label',
      headline: 'navigation.imprint.headline',
      description: 'navigation.imprint.description',
    },
    component: PageImprintComponent,
  },
  {
    path: RouteConstants.routePrivacy,
    title: 'navigation.privacy.label',
    data: {
      breadcrumb: 'navigation.privacy.label',
      headline: 'navigation.privacy.headline',
      description: 'navigation.privacy.description',
    },
    component: PagePrivacyComponent,
  },
  {
    path: RouteConstants.routeTerms,
    title: 'navigation.terms.label',
    data: {
      breadcrumb: 'navigation.terms.label',
      headline: 'navigation.terms.headline',
      description: 'navigation.terms.description',
    },
    component: PageTermsComponent,
  },
  {
    path: RouteConstants.routeError403,
    title: 'navigation.error403.label',
    data: {
      headline: 'navigation.error403.headline',
      error: 403,
    },
    component: PageErrorComponent,
  },
  {
    path: RouteConstants.routeError404,
    title: 'navigation.error404.label',
    data: {
      headline: 'navigation.error404.headline',
      error: 404,
    },
    component: PageErrorComponent,
  },
  {
    path: '**',
    title: 'navigation.error404.label',
    data: {
      headline: 'navigation.error404.headline',
    },
    component: PageErrorComponent,
  },
];
